import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { 
  MapPin, 
  Shield,
  User,
  Globe,
  Mail,
  Copy,
  Check
} from "lucide-react";
import { Link } from "react-router-dom";
import { ExtendedProfessionalEntry } from '@/types/professional';
import { getSpecialtyLabel } from '@/lib/specialtyLabels';

interface ProfessionalCardProps {
  professional: ExtendedProfessionalEntry;
}

export function ProfessionalCard({ professional }: ProfessionalCardProps) {
  const [copied, setCopied] = useState(false);
  const fullName = `${professional.first_name} ${professional.last_name}`;
  
  const getLicenseVerificationBadge = () => {
    if (!professional.license_verification_status) return null;
    
    const isVerified = professional.license_verification_status === 'verified';
    return (
      <Badge 
        variant={isVerified ? "default" : "secondary"}
        className={`shrink-0 ${isVerified ? 'bg-st_light_blue text-white' : ''}`}
      >
        <Shield className="w-3 h-3 mr-1" />
        {isVerified ? 'License Verified' : 'Pending Verification'}
      </Badge>
    );
  };

  const getSpecialtyPills = () => {
    const specialties = professional.specialties || professional.areas_of_expertise || [];
    return specialties.slice(0, 4);
  };

  const getProfessionDisplay = () => {
    switch (professional.profession) {
      case 'Occupational Therapist':
        return 'OT';
      case 'Physical Therapist':
        return 'PT';
      case 'Certified Aging-in-Place Specialist':
        return 'CAPS';
      case 'Elder Law Attorney':
        return 'Elder Law';
      case 'Home Modification Specialist':
        return 'Home Modification';
      case 'Financial Planner':
        return 'Financial Planning';
      default:
        return professional.profession;
    }
  };

  const getLocation = () => {
    if (professional.service_city && professional.service_zip_code) {
      return `${professional.service_city}, ${professional.service_zip_code}`;
    }
    if (professional.service_zip_code) {
      return professional.service_zip_code;
    }
    return professional.license_state || 'Contact for location';
  };

  const getServiceRadius = () => {
    if (professional.service_radius_miles) {
      return `${professional.service_radius_miles} mi radius`;
    }
    return null;
  };

  const truncateBio = (bio: string, maxLength = 120) => {
    if (!bio || bio.length <= maxLength) return bio;
    return bio.substring(0, maxLength).trim() + '...';
  };

  const profileLink = professional.directory_slug 
    ? `/professionals/${professional.directory_slug}` 
    : null;

  const cardContent = (
    <>
      {/* Header */}
      <div className="flex items-start gap-4 mb-4">
        {professional.professional_photo_url ? (
          <img
            src={professional.professional_photo_url}
            alt={`${fullName} profile`}
            className="w-16 h-16 rounded-full object-cover border-2 border-border"
          />
        ) : (
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center border-2 border-border">
            <User className="w-8 h-8 text-muted-foreground" />
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="text-lg font-bold text-foreground truncate group-hover:text-primary transition-colors">
              {fullName}
            </h3>
            {getLicenseVerificationBadge()}
          </div>
          
          {professional.role_title && (
            <p className="text-sm text-muted-foreground italic mb-1">
              {professional.role_title}
            </p>
          )}
          
          <p className="text-sm font-medium text-primary mb-2">
            {getProfessionDisplay()}
          </p>
          
          {professional.bio && (
            <p className="text-sm text-muted-foreground mb-3">
              {truncateBio(professional.bio)}
            </p>
          )}
        </div>
      </div>

      {/* Specialty Pills */}
      {getSpecialtyPills().length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {getSpecialtyPills().map((specialty) => (
            <Badge key={specialty} variant="outline" className="text-xs font-normal">
              {getSpecialtyLabel(specialty)}
            </Badge>
          ))}
        </div>
      )}

      {/* Quick Facts */}
      <div className="flex flex-wrap gap-3 text-sm mb-4">
        <div className="flex items-center gap-1 text-muted-foreground">
          <MapPin className="w-4 h-4" />
          <span className="truncate">{getLocation()}</span>
        </div>
        {getServiceRadius() && (
          <div className="text-muted-foreground">
            <span>{getServiceRadius()}</span>
          </div>
        )}
      </div>
    </>
  );

  return (
    <Card className="h-full transition-all duration-200 hover:shadow-md border-border/50 group rounded-md border bg-white shadow-raised flex flex-col">
      {profileLink ? (
        <Link to={profileLink} className="block flex-1">
          <CardContent className="p-6 pb-0">
            {cardContent}
          </CardContent>
        </Link>
      ) : (
        <CardContent className="p-6 pb-0 flex-1">
          {cardContent}
        </CardContent>
      )}

      {/* CTAs - outside the Link to avoid nested interactive elements */}
      <div className="flex gap-2 px-6 pb-6 pt-4">
        {profileLink ? (
          <Button variant="outline" className="flex-1" size="sm" asChild>
            <Link to={profileLink}>View profile</Link>
          </Button>
        ) : (
          <Button variant="outline" className="flex-1" size="sm" disabled>
            View profile
          </Button>
        )}
        {(professional.email || professional.website_url || professional.company_website) ? (
          <Popover>
            <PopoverTrigger asChild>
              <Button 
                className="flex-1 bg-st_light_blue hover:bg-st_light_blue/90" 
                size="sm"
              >
                Request consult
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-4" align="end">
              <div className="space-y-3">
                <p className="text-sm font-bold text-foreground">Contact {professional.first_name}</p>
                
                {professional.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="text-sm text-foreground truncate flex-1">{professional.email}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 shrink-0"
                      onClick={() => {
                        navigator.clipboard.writeText(professional.email!);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                    >
                      {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                )}

                {(professional.website_url || professional.company_website) && (
                  <Button variant="outline" size="sm" className="w-full" asChild>
                    <a href={professional.website_url || professional.company_website} target="_blank" rel="noopener noreferrer">
                      <Globe className="w-4 h-4 mr-1" />
                      Visit website
                    </a>
                  </Button>
                )}
              </div>
            </PopoverContent>
          </Popover>
        ) : (
          <Button className="flex-1" size="sm" disabled variant="secondary">
            Request consult
          </Button>
        )}
      </div>
    </Card>
  );
}